import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Send, Loader2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { CONTACT, SERVICES } from "../data/translations";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { lang, t } = useLanguage();
  const c = t.contactPage;
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      toast.success(c.successTitle, { description: c.successText });
      setForm({ name: "", company: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(c.errorText);
    } finally {
      setSending(false);
    }
  };

  const infoItems = [
    { icon: MapPin, label: c.addressLabel, value: CONTACT.address, href: null, testid: "contact-info-address" },
    { icon: Phone, label: c.phoneLabel, value: CONTACT.phone, href: CONTACT.phoneHref, testid: "contact-info-phone" },
    { icon: Mail, label: c.emailLabel, value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-info-email" },
    { icon: Clock, label: c.hoursLabel, value: c.hours, href: null, testid: "contact-info-hours" },
  ];

  return (
    <div className="bg-ink" data-testid="contact-page">
      <section className="relative pt-40 pb-16 bg-ink-surface border-b border-white/5 bg-grid" data-testid="contact-header">
        <div className="container-site">
          <p className="eyebrow">{c.eyebrow}</p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white max-w-3xl leading-[1.05]">{c.title}</h1>
          <p className="mt-6 max-w-xl text-slate-400">{c.subtitle}</p>
        </div>
      </section>

      <section className="section-pad" data-testid="contact-section">
        <div className="container-site grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <Reveal className="lg:col-span-5 space-y-5">
            <h2 className="font-display text-2xl font-bold text-white">{c.infoTitle}</h2>
            {infoItems.map(({ icon: Icon, label, value, href, testid }) => (
              <div key={testid} className="card-dark p-6 flex gap-4" data-testid={testid}>
                <div className="w-11 h-11 shrink-0 bg-brand/10 border border-brand/30 flex items-center justify-center text-brand">
                  <Icon size={18} />
                </div>
                <div>
                  <p className="mini-label">{label}</p>
                  {href ? (
                    <a href={href} data-testid={`${testid}-link`} className="mt-1.5 block text-sm text-white hover:text-brand transition-colors">
                      {value}
                    </a>
                  ) : (
                    <p className="mt-1.5 text-sm text-white leading-relaxed">{value}</p>
                  )}
                </div>
              </div>
            ))}
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="card-dark p-8 sm:p-10" data-testid="contact-form-card">
              <h2 className="font-display text-2xl font-bold text-white">{c.formTitle}</h2>
              <p className="mt-2 text-sm text-slate-400">{c.formSubtitle}</p>
              <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-5" data-testid="contact-form">
                <div>
                  <label className="field-label" htmlFor="name">{c.name} *</label>
                  <input id="name" data-testid="contact-input-name" className="field-input" value={form.name} onChange={update("name")} required placeholder={c.namePh} />
                </div>
                <div>
                  <label className="field-label" htmlFor="company">{c.company}</label>
                  <input id="company" data-testid="contact-input-company" className="field-input" value={form.company} onChange={update("company")} placeholder={c.companyPh} />
                </div>
                <div>
                  <label className="field-label" htmlFor="email">{c.email} *</label>
                  <input id="email" type="email" data-testid="contact-input-email" className="field-input" value={form.email} onChange={update("email")} required placeholder={c.emailPh} />
                </div>
                <div>
                  <label className="field-label" htmlFor="phone">{c.phone}</label>
                  <input id="phone" type="tel" data-testid="contact-input-phone" className="field-input" value={form.phone} onChange={update("phone")} placeholder={c.phonePh} />
                </div>
                <div className="sm:col-span-2">
                  <label className="field-label" htmlFor="service">{c.service}</label>
                  <select id="service" data-testid="contact-select-service" className="field-input" value={form.service} onChange={update("service")}>
                    <option value="">{t.general.generalEnquiry}</option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.id}>{s[lang].title}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="field-label" htmlFor="message">{c.message} *</label>
                  <textarea id="message" rows={5} data-testid="contact-input-message" className="field-input resize-none" value={form.message} onChange={update("message")} required placeholder={c.messagePh} />
                </div>
                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                  <p className="text-xs text-slate-500">{c.required}</p>
                  <button type="submit" disabled={sending} data-testid="contact-submit-button" className="btn-primary w-full sm:w-auto disabled:opacity-60 disabled:pointer-events-none">
                    {sending ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> {c.sending}
                      </>
                    ) : (
                      <>
                        <Send size={16} /> {c.submit}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </Reveal>
        </div>

        <div className="container-site mt-12">
          <div className="border border-white/10 overflow-hidden" data-testid="contact-map">
            <iframe
              title="MVALOG — Mersin Office Map"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(CONTACT.mapQuery)}&z=15&output=embed`}
              className="w-full h-80 grayscale-[1] invert-[92%] contrast-[85%]"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
